import { service } from '@ember/service'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { themeKeys } from 'teacher-ui-client/components/giraffe/stat-box/themes'
import Component from '@glimmer/component'
import type { IntlService } from 'ember-intl'
import { StatBox } from 'teacher-ui-client/components/giraffe/stat-box'
import type { PhosphorIconName } from 'phosphor-icons'

interface SummaryCategoryStats {
  correct_answers: number
  improvement: number
  sprints_mastered: number
}

export interface MentalMinuteSummaryData {
  aggregate: {
    correct_answers: number
    sprints_mastered: number
    improvement: number
  }
  summary: {
    division: SummaryCategoryStats
    multiplication: SummaryCategoryStats
    addition: SummaryCategoryStats
    subtraction: SummaryCategoryStats
  }
}

interface Signature {
  Args: {
    data: MentalMinuteSummaryData
    dataScope: string
  }
  Element: Element
}

interface SummaryCategory {
  category: 'addition' | 'subtraction' | 'multiplication' | 'division'
  titleIcon?: PhosphorIconName
  theme: (typeof themeKeys)[keyof typeof themeKeys]
}

export class StatBoxesFluencySprints extends Component<Signature> {
  @service intl!: IntlService

  get dataScope() {
    return this.args.dataScope // student | schoolClass | teacher | school
  }

  summaryCategories: SummaryCategory[] = [
    {
      category: 'addition',
      titleIcon: 'phosphor-plus-bold',
      theme: themeKeys.THEME_OCEANY_BLUE,
    },
    {
      category: 'subtraction',
      titleIcon: 'phosphor-minus-bold',
      theme: themeKeys.THEME_JUICY_ORANGE,
    },
    {
      category: 'multiplication',
      titleIcon: 'phosphor-x-bold',
      theme: themeKeys.THEME_MS_GREEN,
    },
    {
      category: 'division',
      titleIcon: 'phosphor-divide-bold',
      theme: themeKeys.THEME_PURPLY_PINK,
    },
  ]

  get aggregateBox() {
    const sprintsMasteredValue = this.args.data.aggregate.sprints_mastered
    const correctAnswersValue = this.args.data.aggregate.correct_answers
    const improvementValue = this.args.data.aggregate.improvement

    return {
      category: 'sprints',
      title: this.intl.t(`reporting.maths.fluencySprints.statBoxes.aggregate.sprints.title.${this.dataScope}`),
      theme: themeKeys.THEME_MID_ORANGE,
      stats: [
        [
          {
            name: this.intl.t('reporting.maths.fluencySprints.statBoxes.aggregate.statCategories.sprints_mastered'),
            value: formatValue(sprintsMasteredValue),
          },
        ],
        [
          {
            name: this.intl.t('reporting.maths.fluencySprints.statBoxes.aggregate.statCategories.correct_answers'),
            value: formatValue(correctAnswersValue),
          },
          {
            name: this.intl.t('reporting.maths.fluencySprints.statBoxes.aggregate.statCategories.improvement'),
            value: formatValue(improvementValue, { append: '%' }),
          },
        ],
      ],
    }
  }

  get summaryBoxes() {
    return this.summaryCategories.map((categoryObj) => {
      const { category, titleIcon, theme } = categoryObj
      const title = this.intl.t(`reporting.maths.fluencySprints.statBoxes.summary.${category}.title`)

      const sprintsMasteredValue = this.args.data.summary[category].sprints_mastered
      const correctAnswersValue = this.args.data.summary[category].correct_answers
      const improvementValue = this.args.data.summary[category].improvement

      const stats = [
        [
          {
            name: this.intl.t('reporting.maths.fluencySprints.statBoxes.summary.statCategories.sprints_mastered'),
            value: formatValue(sprintsMasteredValue),
          },
        ],
        [
          {
            name: this.intl.t('reporting.maths.fluencySprints.statBoxes.summary.statCategories.correct_answers'),
            value: formatValue(correctAnswersValue),
          },
          {
            name: this.intl.t('reporting.maths.fluencySprints.statBoxes.summary.statCategories.improvement'),
            value: formatValue(improvementValue, { append: '%' }),
          },
        ],
      ]

      return { category, titleIcon, title, theme, stats }
    })
  }

  <template>
    <div class="flex flex-wrap justify-center" ...attributes>
      <StatBox
        data-test-stat-box={{this.aggregateBox.category}}
        class="max-w-[325px] min-w-[210px] shrink grow basis-[20%] border-r border-b border-white print:mb-[10px] [&_stat\_\_value]:text-[26px] [&_stats]:text-[26px]"
        @title={{this.aggregateBox.title}}
        @theme={{this.aggregateBox.theme}}
        @stats={{this.aggregateBox.stats}}
      />
      {{#each this.summaryBoxes as |summaryBox|}}
        <StatBox
          data-test-stat-box={{summaryBox.category}}
          class="max-w-[325px] min-w-[210px] shrink grow basis-[20%] border-r border-b border-white print:mb-[10px] [&_stat\_\_value]:text-[26px] [&_stats]:text-[26px]"
          @title={{summaryBox.title}}
          @theme={{summaryBox.theme}}
          @titleIcon={{summaryBox.titleIcon}}
          @stats={{summaryBox.stats}}
        />
      {{/each}}
    </div>
  </template>
}

export default StatBoxesFluencySprints
